import PropTypes from 'prop-types'
import { cn } from '@/lib/utils'
import React from 'react'
import { HelpCircle } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@radix-ui/react-tooltip'

const InputFieldObfuscated = React.forwardRef(
  (
    {
      icon,
      label,
      tooltipHead,
      tooltipBody,
      className,
      helpText,
      onBlur,
      touched,
      type,
      error,
      dataTestId,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const [maskedValue, setMaskedValue] = React.useState(value)
    const [masked, setMasked] = React.useState(true) // Flag for converting input type  password <-> text
    const [originalValue, setOriginalValue] = React.useState(value)

    const handleChange = e => {
      const inputValue = e.target.value
      const lastChar = inputValue.slice(-1)
      let newValue

      if (inputValue.length < originalValue.length) {
        // Remove last character
        newValue = originalValue.slice(0, -1)
      } else {
        // Append new character
        newValue = originalValue + lastChar
      }

      // Update original value state with the actual input
      setOriginalValue(newValue)

      // update the masked value state with the modified value to show only the last digit
      setMaskedValue(maskValue(newValue))

      // Switch the input type when user starts typing
      setMasked(false)

      // Mask again after showing the last digit for a short period
      if (inputValue.length > 0) {
        setTimeout(() => {
          setMasked(true) // Mask everything after 2 seconds (text -> password)
        }, 2000)
      }
      onChange(newValue) // Updating Formik value
    }

    const handleBlur = e => {
      console.log('Handle blurred triggered')
      setMasked(true)
      onBlur && onBlur(e) // Formik's onBlur trigger
    }

    /* 
    Takes in a string mask everyting expect the last char 
    hello -> •••• + o -> ••••o 
    */
    const maskValue = val => {
      if (!val) return ''

      return val.slice(0, -1).replace(/./g, '•') + val[val.length - 1]
    }

    return (
      <div className="flex flex-col items-start self-stretch gap-1.5" data-testid="input-field-obfuscated">
        <p className="text-sm font-medium text-[--gray-700]">{label}</p>
        <div
          className={cn(
            'flex min-h-10 items-center rounded-md border border-input bg-white pl-3 text-base w-full',
            error && touched ? 'border-[--error-300]' : '',
            className
          )}
          onBlur={handleBlur} // Handle blur event
        >
          {icon}
          <input
            onChange={e => handleChange(e)}
            value={maskedValue}
            ref={ref}
            // onBlur={handleBlur}
            type={masked ? 'password' : type}
            className={`w-full placeholder:text-[--gray-500] placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${
              icon ? 'p-2' : ''
            }`}
            {...props}
            data-testid={dataTestId}
          />
          <div className="px-2">
            {tooltipHead || tooltipBody ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle
                      className="w-4 h-4 cursor-pointer"
                      color="#98A2B3"
                      name="help-icon"
                      data-testid="help-icon"
                    />
                  </TooltipTrigger>
                  <TooltipContent className="mb-1 flex flex-col font-sans bg-[#101828] p-3 rounded-md max-w-80 gap-1">
                    <p className="text-white text-xs font-[600]">{tooltipHead}</p>
                    <p className="text-[#D0D5DD] text-xs font-[500]">{tooltipBody}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
          </div>
        </div>
        {touched && error && <p className="text-sm font-normal text-[--error-500]">{error}</p>}
        {helpText}
      </div>
    )
  }
)

InputFieldObfuscated.displayName = 'InputWithValueObfuscated'

export { InputFieldObfuscated }

InputFieldObfuscated.propTypes = {
  icon: PropTypes.node,
  helpText: PropTypes.node,
  className: PropTypes.string,
  onBlur: PropTypes.func,
  touched: PropTypes.bool,
  error: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  tooltipHead: PropTypes.string,
  tooltipBody: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  type: PropTypes.string,
  dataTestId: PropTypes.string,
}
